import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import PdfViewer from '../../../components/shared/pdfViewer/PdfViewer';
import './UploadedDocumentsEdit.scss';
import Breadcrumbs from '../../../components/shared/breadcrumbs/Breadcrumbs';
import DocumentFields from '../../../components/shared/DocumentFields/DocumentFields';
import {
    useGetV1DocumentClassificationGetClassification,
    usePostV1DocumentClassificationUpdateClassification,
    useGetV1ProcessGetUploadedDocumentsInProcessProcessId,
} from '../../../api/generated/endpoint';
import { UpdateClassificationDTO } from '../../../api/generated/models/updateClassificationDTO';
import useToast from '../../../helpers/useToast';
import useLocale from '../../../locale/useLocale';
import { Loading } from '@appkit4/react-components';
import { ProcessUploadedDocumentsResponse } from '../../../api/generated/models';

const UploadedDocumentsEdit = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { processId, documentId, status } = useParams();
    const { listingTab } = location.state || {};
    const [currentPage, setCurrentPage] = useState(1);
    const showToast = useToast();
    const { l } = useLocale();
    const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isProceeding, setIsProceeding] = useState(false);

    const activeTab = location.state?.activeTab || 0;
    const parentName = location.state?.parentName || '';

    const { data: processDocuments, refetch: refetchProcessDocuments } =
        useGetV1ProcessGetUploadedDocumentsInProcessProcessId(
            Number(processId),
            undefined,
            {
                query: {
                    enabled: processId !== null,
                },
            }
        );
    const [documents, setDocuments] = useState<
        ProcessUploadedDocumentsResponse[]
    >([]);

    const uploadedListingTab = location.state?.uploadedListingTab || 0;

    const getFilteredDocuments = (data: ProcessUploadedDocumentsResponse[]) => {
        const editableDocuments = data.filter(
            (doc) =>
                doc.uploadedDocumentStatus !== undefined &&
                [5, 6].includes(doc.uploadedDocumentStatus)
        );

        if (uploadedListingTab === 1) {
            return editableDocuments.filter(
                (doc) => doc.uploadedDocumentStatus === 5
            );
        } else if (uploadedListingTab === 2) {
            return editableDocuments.filter(
                (doc) => doc.uploadedDocumentStatus === 6
            );
        }
        return editableDocuments;
    };

    useEffect(() => {
        if (
            processDocuments &&
            Array.isArray(processDocuments.uploadedDocuments)
        ) {
            const filteredDocuments = getFilteredDocuments(
                processDocuments.uploadedDocuments
            );
            setDocuments(filteredDocuments);

            // Find the index of the current document in the filtered list
            const index = filteredDocuments.findIndex(
                (doc) => doc.uploadedDocumentId === parseInt(documentId || '0')
            );
            setCurrentDocumentIndex(index !== -1 ? index : 0);
        }
    }, [processDocuments, documentId, uploadedListingTab]);

    const [breadcrumbItems, setBreadcrumbItems] = useState([
        { label: 'Process', href: '' },
        { label: 'Document' },
    ]);
    const [documentFields, setDocumentFields] = useState<any[]>([]);
    const [totalPages, setTotalPages] = useState<any>();

    const {
        data: classificationData,
        isLoading: classificationDataLoading,
        isError,
        error,
    } = useGetV1DocumentClassificationGetClassification(
        { UploadedDocumentId: Number(documentId) },
        {
            query: {
                enabled: !!documentId,
                refetchOnWindowFocus: false,
                refetchOnReconnect: false,
                refetchOnMount: false,
                staleTime: Infinity,
                cacheTime: Infinity,
            },
        }
    );

    const updateClassificationMutation =
        usePostV1DocumentClassificationUpdateClassification();

    const mapStatusToUiStatus = (status?: boolean) => {
        switch (status) {
            case false:
                return 'AIApproved';
            case true:
                return 'AIRejected';
            default:
                return 'Unknown';
        }
    };

    useEffect(() => {
        if (classificationData) {
            setTotalPages(classificationData.totalPagesCount);
            setBreadcrumbItems([
                {
                    label: classificationData.processName || 'Process',
                    href: `/process/${
                        classificationData.processId || processId
                    }/uploaded`,
                },
                {
                    label:
                        classificationData.uploadedDocumentName || 'Document',
                },
            ]);

            if (
                classificationData.classifications &&
                classificationData.classifications.length > 0
            ) {
                const mappedFields = classificationData.classifications.map(
                    (classification) => ({
                        id: String(classification.classificationId),
                        status: mapStatusToUiStatus(
                            classification.confidenceWithinThreshold
                        ),
                        documentType:
                            classification.documentTypeName || 'Unknown',
                        documentTypeId: classification.documentTypeId,
                        documentRange: {
                            start: classification.startPage || 1,
                            end: classification.endPage || 1,
                        },
                        confidenceScore: classification.confidenceScore,
                        confidenceWithinThreshold:
                            classification.confidenceWithinThreshold,
                        totalPages: classification.totalPages,
                        lowerConfidenceThresholdAtTime:
                            classification.lowerConfidenceThresholdAtTime,
                        upperConfidenceThresholdAtTime:
                            classification.upperConfidenceThresholdAtTime,
                        createdDateTime: classification.createdDateTime,
                        statusChangedDateTime:
                            classification.statusChangedDateTime,
                    })
                );
                const sortedFields = mappedFields.sort(
                    (a, b) => a.documentRange.start - b.documentRange.start
                );
                setDocumentFields(sortedFields);
            }
        }
    }, [classificationData]);

    const handleFieldUpdate = (updatedField: any) => {
        setDocumentFields((fields) =>
            fields.map((field) =>
                field.id === updatedField.id ? updatedField : field
            )
        );
    };

    const handleFieldRemove = (fieldId: string) => {
        setDocumentFields((fields) =>
            fields.filter((field) => field.id !== fieldId)
        );
    };
    const generateTempId = () =>
        `temp-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

    const handleFieldAdd = () => {
        const newField: any = {
            id: generateTempId(),
            status: 'Unknown',
            documentType:
                classificationData?.availableDocumentTypes?.[0]
                    ?.documentTypeName || 'Unknown',
            documentTypeId:
                classificationData?.availableDocumentTypes?.[0]?.documentTypeId,
            viewPage: 1,
            documentRange: { start: 1, end: 1 },
        };
        setDocumentFields((fields) => [...fields, newField]);
    };
    const handleProceed = async () => {
        setIsSubmitting(true);
        try {
            const originalClassifications =
                classificationData?.classifications || [];
            const classifications: any[] = [];
            const updatedIds = new Set(
                documentFields.map((f) => f.id).filter(Boolean)
            );
            for (const original of originalClassifications) {
                const originalId = String(original.classificationId);
                if (!updatedIds.has(originalId)) {
                    classifications.push({
                        classificationId: original.classificationId,
                        classificationStatus: 4, // Human Rejected
                        documentTypeId: original.documentTypeId,
                        startPage: original.startPage || 1,
                        endPage: original.endPage || 1,
                    });
                }
            }

            // 2. Handle added/modified/unchanged classifications
            for (const field of documentFields) {
                const isExisting = Boolean(field.id);
                const original = originalClassifications.find(
                    (c) => String(c.classificationId) === String(field.id)
                );

                if (isExisting && original) {
                    // If unchanged or modified → send with status = 3
                    classifications.push({
                        classificationId: original.classificationId,
                        classificationStatus: 3, // Human Approved
                        documentTypeId: field.documentTypeId,
                        startPage: field.documentRange.start,
                        endPage: field.documentRange.end,
                    });
                } else {
                    // New classification added by user → classificationId = null
                    classifications.push({
                        classificationId: null,
                        classificationStatus: 3, // Human Approved
                        documentTypeId: field.documentTypeId,
                        startPage: field.documentRange.start,
                        endPage: field.documentRange.end,
                    });
                }
            }

            const updateDTO: UpdateClassificationDTO = {
                classifications,
                uploadedDocumentId: Number(documentId),
            };
            await updateClassificationMutation.mutateAsync({ data: updateDTO });

            showToast(
                l('_success'),
                'Updated Classification Successfully',
                'success'
            );
            if (processId) {
                const tab = typeof listingTab === 'number' ? listingTab : 0;
                navigate(`/process/${processId}/uploaded`, {
                    state: {
                        listingTab: tab,
                        uploadedListingTab: uploadedListingTab,
                    },
                });
            }
        } catch (err) {
            console.error('Error Updating Classifications:', err);
            showToast(l('_error'), 'Failed to update document', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getNextDocument = async () => {
        if (documents.length === 0) return;

        const nextIndex = (currentDocumentIndex + 1) % documents.length;
        const nextDocument = documents[nextIndex];

        navigate(
            `/process/${processId}/uploaded/${nextDocument.uploadedDocumentId}`,
            {
                state: {
                    documentId: nextDocument.uploadedDocumentId,
                    parentLocation: `/process/${processId}/uploaded`,
                    fileName: nextDocument.fileName,
                    parentName: parentName,
                    activeTab: activeTab,
                    uploadedListingTab: uploadedListingTab,
                },
            }
        );
    };

    const handleNext = async () => {
        setIsProceeding(true);
        try {
            const originalClassifications =
                classificationData?.classifications || [];

            const classifications: any[] = [];

            // Track existing field IDs for comparison
            const updatedIds = new Set(
                documentFields.map((f) => f.id).filter(Boolean)
            );

            // 1. Handle removed classifications (exist in original but not in current fields)
            for (const original of originalClassifications) {
                const originalId = String(original.classificationId);
                if (!updatedIds.has(originalId)) {
                    classifications.push({
                        classificationId: original.classificationId,
                        classificationStatus: 4, // Human Rejected
                        documentTypeId: original.documentTypeId,
                        startPage: original.startPage || 1,
                        endPage: original.endPage || 1,
                    });
                }
            }

            // 2. Handle added/modified/unchanged classifications
            for (const field of documentFields) {
                const isExisting = Boolean(field.id);
                const original = originalClassifications.find(
                    (c) => String(c.classificationId) === String(field.id)
                );

                if (isExisting && original) {
                    // If unchanged or modified → send with status = 3
                    classifications.push({
                        classificationId: original.classificationId,
                        classificationStatus: 3, // Human Approved
                        documentTypeId: field.documentTypeId,
                        startPage: field.documentRange.start,
                        endPage: field.documentRange.end,
                    });
                } else {
                    // New classification added by user → classificationId = null
                    classifications.push({
                        classificationId: null,
                        classificationStatus: 3, // Human Approved
                        documentTypeId: field.documentTypeId,
                        startPage: field.documentRange.start,
                        endPage: field.documentRange.end,
                    });
                }
            }

            const updateDTO: UpdateClassificationDTO = {
                classifications,
                uploadedDocumentId: Number(documentId),
            };
            await updateClassificationMutation.mutateAsync({ data: updateDTO });

            showToast(
                l('_success'),
                'Updated Classification Successfully',
                'success'
            );
            await refetchProcessDocuments();
            await new Promise((resolve) => setTimeout(resolve, 3000));
            getNextDocument();
        } catch (err) {
            console.error('Error Updating Classifications:', err);
            showToast(l('_error'), 'Failed to update document', 'error');
        } finally {
            setIsProceeding(false);
        }
    };

    const handleViewPage = (page: number) => {
        setCurrentPage(page);
    };

    if (classificationDataLoading) {
        return (
            <div className="process-edit-container">
                <Loading loadingType="circular" indeterminate compact={false} />
            </div>
        );
    }

    if (isError) {
        return (
            <div className="process-edit-container">
                <div className="error-message">
                    <h3>API Error</h3>
                    <p>
                        {error?.message ||
                            JSON.stringify(error) ||
                            'An unknown error occurred'}
                    </p>
                </div>
            </div>
        );
    }

    if (!classificationData) {
        return (
            <div className="process-edit-container">
                <div className="no-data-message">
                    <h3>No classification data available</h3>
                </div>
            </div>
        );
    }

    return (
        <div className="process-edit-container">
            <Breadcrumbs items={breadcrumbItems} />
            <div>
                <h2 className="ap-typography-heading-l title">
                    {classificationData?.uploadedDocumentName}
                </h2>
            </div>
            <div className="process-edit">
                <div className="pdf-section">
                    <PdfViewer
                        currentPage={currentPage}
                        fileUrl={classificationData?.sasUrl || ''}
                    />
                </div>
                <div className="edit-section">
                    <DocumentFields
                        fields={documentFields}
                        onFieldUpdate={handleFieldUpdate}
                        onFieldRemove={handleFieldRemove}
                        onFieldAdd={handleFieldAdd}
                        onProceed={handleProceed}
                        onNext={handleNext}
                        onViewPage={handleViewPage}
                        totalPages={totalPages}
                        availableDocumentTypes={
                            classificationData?.availableDocumentTypes || []
                        }
                        isSubmitting={isSubmitting}
                        isProceeding={isProceeding}
                        hasNextDocument={documents.length > 1}
                        documentView={status == 'viewonly'}
                    />
                </div>
            </div>
        </div>
    );
};

export default UploadedDocumentsEdit;
